import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Stack,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material';

interface LocationState {
  errorMessage?: string;
  errorDetails?: string;
  returnPath?: string;
}

function ErrorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const errorMessage =
    state?.errorMessage ||
    'Something went wrong while loading the requested resource.';
  const errorDetails = state?.errorDetails || 'An unexpected error occurred.';
  const returnPath = state?.returnPath || '/';

  const handleTryAgain = () => {
    // Navigate to the original page first, then reload
    navigate(returnPath, { replace: true });
    // Use setTimeout to ensure navigation completes before reload
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth='md'>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 600,
            width: '100%',
          }}
        >
          <Typography
            variant='h3'
            component='h1'
            color='primary'
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Oops!
          </Typography>

          <Typography variant='h5' component='h2' gutterBottom sx={{ mb: 3 }}>
            Something went wrong
          </Typography>

          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ mb: 4, lineHeight: 1.6 }}
          >
            {errorMessage}
          </Typography>

          {errorDetails && (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                mb: 4,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}
            >
              {errorDetails}
            </Typography>
          )}

          <Typography variant='body2' color='text.secondary' sx={{ mb: 4 }}>
            Please try again or contact your system administrator if the problem
            persists.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='center'
            alignItems='center'
          >
            <Button
              variant='contained'
              color='primary'
              startIcon={<RefreshIcon />}
              onClick={handleTryAgain}
              size='large'
            >
              Try Again
            </Button>

            <Button
              variant='outlined'
              color='primary'
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              size='large'
            >
              Go Home
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}

export default ErrorPage;
